import { Box, LoadingOverlay, Text } from '@mantine/core';

import { useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { IconLogout2 } from '@tabler/icons-react';

import ModalContent from '../ModalContent';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import CenteredLoader from '../CenteredLoader';
import useEnsisMutation from '../../hooks/useEnsisMutation';

interface Props {
  close: () => void
  proposalUid?: string
  proposalStatus?: string
};

const MoveProposalToAuthoring: React.FC<Props> = ({ close, proposalUid, proposalStatus }: Props) => {
  const { organization } = useParams();
  const { data: requirementResponseData, isLoading: requirementResponseLoading } = useEnsisQuery(
    '/app/requirement-responses',
    {
      queryParams: { proposal_uid: proposalUid }
    }
  );
  const generateAllProposalSections = useEnsisMutation(
    `/app/proposals/${proposalUid}/generate-all-sections`, {
      showSuccessMessage: false,
      queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`]
    }
  );
  const navigate = useNavigate();
  const handleModalOnContinue = useCallback(() => {
    generateAllProposalSections.mutate({});
  }, [generateAllProposalSections]);

  const numberOfUnassignedRequirements = requirementResponseData?.items?.filter(
    (req) => req?.proposal_section === null
  ).length ?? 0;
  const numberOfSortedRequirements = requirementResponseData?.items?.filter(
    (req) => req?.proposal_section !== null
  ).length ?? 0;

  const noRequirementsSorted = numberOfSortedRequirements === 0;
  const noRequirementsSortedTitle = 'No Requirements Sorted';
  const noRequirementsSortedText = (
    <Box mb={24}>
      <Text>
        {'You haven\'t added any requirements to your outline. Are you sure you want to continue?'}
      </Text>
      <Text mt={24}>
        {'Note: You can always add these requirements later by going to: Proposal Management > Outline'}
      </Text>
    </Box>
  );

  const unassignedRequirementsRemaining = numberOfUnassignedRequirements > 0;
  const unassignedRequirementsRemainingTitle = `${numberOfUnassignedRequirements} Unsorted Requirements`;
  const unassignedRequirementsRemainingText = (
    <Box mb={24}>
      <Text>
        {`You have ${numberOfUnassignedRequirements} requirements that weren't added to your outline.
        Are you sure you want to continue?`}
      </Text>
      <Text mt={24}>
        {'Note: You can always add these requirements later by going to: Proposal Management > Outline'}
      </Text>
    </Box>
  );

  const defaultTitle = 'Ready to Start Authoring?';
  const defaultText = (<Text mb={24}>
    Once you move to authoring, you can modify your outline from the Proposal Management page.
  </Text>);

  const modalTitle = noRequirementsSorted
    ? noRequirementsSortedTitle
    : (unassignedRequirementsRemaining ? unassignedRequirementsRemainingTitle : defaultTitle);
  const modalText = noRequirementsSorted
    ? noRequirementsSortedText
    : (unassignedRequirementsRemaining ? unassignedRequirementsRemainingText : defaultText);

  if (proposalStatus === 'AUTHORING') {
    navigate(`/org/${organization}/proposals/${proposalUid}`);
    close();
  }
  if (proposalStatus === 'GENERATING') {
    return (
      <ModalContent
        title='Generating your responses'
        subtitle={'Your first section is being prepared. This can take about a minute.' +
          'Hang tight, and your draft will be ready shortly.'}
        leftSideButton={{
          label: 'Exit Proposal',
          leftSection: <IconLogout2 />,
          onClick: () => { navigate(`/org/${organization}/proposals`); close(); }
        }}
      >
        <CenteredLoader h={300} loaderProps={{ size: '100' }} />
      </ModalContent>
    );
  }

  if (requirementResponseLoading) {
    return <CenteredLoader />;
  }

  return (<ModalContent
    title={modalTitle}
    primaryButton={{
      label: 'Continue to Authoring',
      onClick: handleModalOnContinue
    }}
    secondaryButton={{
      label: 'Go Back',
      onClick: close
    }}
    onClose={close}
  >
    <LoadingOverlay visible={generateAllProposalSections.isPending} />
    {modalText}
  </ModalContent>);
};

export default MoveProposalToAuthoring;
